import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import debounce from "lodash/debounce";
import { object, array } from "yup";
import { Button, Row, Space, Spinner, Form } from "@masterteam/mt-design-react";
import AdminFormModal from "../../AdminFormModal";

import GroupsPicker from "../../GroupsPicker";
import { ReactComponent as NewUserIcon } from "../../../assets/images/newuser.svg";

import { addToast } from "../../../helpers";
import api from "../../../providers/api";

const { Field } = Form;

const validationSchema = object().shape({
  User: object().required(" ").nullable(),
  Groups: array().min(1, " "),
});

function AddUser({ selectedUser, showModal, closeHandler, callback }) {
  const intl = useIntl();
  const editMode = !!selectedUser;
  const [initialState, setInitialState] = useState({
    User: null,
    Groups: [],
  });
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(true);

  //this will call the get all Active Directory users contains the userName passed to the function
  async function validateUserName(userName, callback) {
    let value = null;
    try {
      const res = await api.user.validateUsername({ UserName: userName });
      const { StatusCode, Data, StatusMessage } = res;
      if (StatusCode === "Success") {
        value = res.Data;
        setUsers(Data);
      } else {
        addToast.error(StatusMessage, {
          toastId: "get-users-error",
        });
      }
    } catch (error) {
      addToast.error(error.message, {
        toastId: "get-users-error",
      });
    }
    callback(value);
  }

  //this will call the add user service, it will pass the selected groups to the user object first then well call the service
  function addUserToSP(values, { setIsSubmitting }) {
    const body = {
      ...values.User,
      Groups: values.Groups.map((item) => {
        return { ID: item.ID };
      }),
    };
    const service = editMode ? "update" : "create";
    api.user[service](body)
      .then((res) => {
        const { StatusCode, Data, StatusMessage } = res;
        if (StatusCode === "Success") {
          if (!Data || StatusMessage?.includes("The user is already exist")) {
            addToast.error("user-management.user-exist");
          } else {
            addToast.success("user-management.user-added-successfuly");
            Data.IsDeletable = true;
            callback(Data, editMode);
          }
        } else {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        // addToast.error(error.message, {
        //   toastId: "get-users-error",
        // });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  }

  //this will get all the groups
  function getGroups() {
    api.group
      .getAll()
      .then((res) => {
        const { StatusCode, Data } = res;
        if (StatusCode === "Success") {
          setGroups(Data);
          setGroupsLoading(false);
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      });
  }

  function getUserData() {
    setUserLoading(true);
    if (editMode)
      api.user
        .getById({ username: selectedUser.UserName })
        .then((userRes) => {
          if (userRes?.StatusCode === "Success") {
            const { Data } = userRes;
            setInitialState({
              User: { ...Data },
              Groups: Data.Groups || [],
            });
          }
        })
        .catch((error) => {
          addToast.error(error.message, {
            toastId: "get-users-error",
          });
        })
        .finally(() => {
          setUserLoading(false);
        });
  }

  useEffect(() => {
    if (editMode) {
      getUserData();
    } else {
      setUserLoading(false);
    }
    getGroups();
  }, []);

  return (
    <AdminFormModal
      isOpen={showModal}
      onClose={closeHandler}
      title={intl.formatMessage({ id: "common.user" })}
      isLoading={userLoading}
      Icon={NewUserIcon}
    >
      <Form
        initialValues={initialState}
        validationSchema={validationSchema}
        onSubmit={addUserToSP}
      >
        {({ values, isSubmitting, isValid, setFieldValue, errors }) => (
          <>
            <div className="user-replace-form-container add-user-form">
              <Space direction="vertical" size="medium" className="form-body">
                <Space
                  direction="vertical"
                  size="medium"
                  className="form-container"
                >
                  <div className="d-flex picker-container">
                    <div className="d-flex full-width">
                      {!editMode ? (
                        <Row size="small">
                          <Field
                            type="async-select"
                            name="User"
                            label={intl.formatMessage({ id: "user.username" })}
                            inputProps={{
                              loadOptions: debounce(validateUserName, 500),
                              defaultOptions: users?.length ? users : false,
                              options: users,
                              placeholder: intl.formatMessage({
                                id: "user.username-placeholder",
                              }),
                              cacheOptions: true,
                              isClearable: true,
                              getOptionLabel: (item) => item.DisplayName,
                              getOptionValue: (item) => item.UserName,
                            }}
                            required
                          />
                        </Row>
                      ) : (
                        <label className="selected-user-name">
                          {selectedUser.DisplayName}
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="line"></div>
                  {!groupsLoading ? (
                    <GroupsPicker
                      list={groups}
                      selected={values.Groups}
                      onChange={(value) => setFieldValue("Groups", value)}
                      label1={intl.formatMessage({ id: "user.list-of-groups" })}
                      label2={intl.formatMessage({
                        id: "user.selected-groups",
                      })}
                      valueKey="ID"
                      nameKey="Title"
                      error={errors.Groups}
                    />
                  ) : (
                    <Row justify="center">
                      <Spinner />
                    </Row>
                  )}
                </Space>
                <Row size="small" justify="end">
                  <Button variant="dark" onClick={closeHandler}>
                    {intl.formatMessage({ id: "common.cancel" })}
                  </Button>
                  <Button
                    loading={isSubmitting}
                    type="submit"
                    disabled={!isValid}
                    fixed
                  >
                    {intl.formatMessage({
                      id: editMode ? "common.update" : "common.add",
                    })}
                  </Button>
                </Row>
              </Space>
            </div>
          </>
        )}
      </Form>
    </AdminFormModal>
  );
}

export default AddUser;
